<!--
  One service of Settings → Dependency Sync, as a tile: who it is, which way
  the data goes, and its state in one word and one sentence, readable from the
  other side of the room. Click = open its detail below the board.

  The state object comes from healthOf() (services.js), shared with the page
  banner: the tile and the banner can never say two different things.
-->
<template>
  <div class="ds-tile" :class="[`is-${health.tone || 'none'}`, { 'is-selected': selected, 'is-planned': !service.live }]"
       role="button" tabindex="0" :aria-pressed="selected ? 'true' : 'false'"
       @click="$emit('select', service.key)" @keyup.enter="$emit('select', service.key)">
    <div class="ds-tile-head">
      <div class="ds-tile-id">
        <div class="ds-tile-name">{{ service.name }}</div>
        <div class="ds-tile-topic">{{ service.topic }}</div>
      </div>
      <div class="ds-flow" :title="`From ${service.from} to ${service.to}`">
        <span>{{ service.from }}</span>
        <feather-icon icon="ArrowRightIcon" svgClasses="w-3 h-3" />
        <span>{{ service.to }}</span>
      </div>
    </div>

    <div class="ds-tile-state">
      <feather-icon :icon="health.icon" svgClasses="w-6 h-6" class="ds-tile-state-icon"
                    :class="{ 'is-spinning': health.icon === 'LoaderIcon' }" />
      <div class="ds-tile-state-text">
        <div class="ds-tile-state-label">{{ health.label }}</div>
        <div class="ds-tile-state-line">{{ health.line }}</div>
      </div>
    </div>

    <template v-if="service.live && src">
      <div class="ds-tile-facts">
        <div class="ds-fact">
          <span class="ds-fact-k">Last run</span>
          <span class="ds-fact-v">
            <template v-if="src.last_run">
              {{ roWhen(src.last_run.started_at) }}
              <span class="ds-word" :class="`is-${runTone(src.last_run.status)}`">{{ runLabel(src.last_run.status) }}</span>
            </template>
            <template v-else>never</template>
          </span>
        </div>
        <div class="ds-fact">
          <span class="ds-fact-k">Next run</span>
          <span class="ds-fact-v">{{ nextText }}</span>
        </div>
      </div>
      <slot-strip :slots="src.slots" mode="compact" />
      <div v-for="(m, i) in metrics" :key="i" class="ds-tile-metric" :class="m.tone ? `is-${m.tone}` : ''">
        <feather-icon v-if="m.tone" icon="AlertTriangleIcon" svgClasses="w-3 h-3" class="mr-1" />{{ m.text }}
      </div>
    </template>

    <template v-else-if="!service.live">
      <p class="ds-tile-what">{{ service.what }}</p>
      <div class="ds-tile-metric">Will replace {{ service.replaces }}.</div>
    </template>
  </div>
</template>

<script>
import SlotStrip from './SlotStrip'
import { roWhen } from '@/helpers/romaniaTime'
import { runLabel, runTone } from '../services'

export default {
  name: 'ServiceTile',
  components: { SlotStrip },
  props: {
    service: { type: Object, required: true },
    src: { type: Object, default: null },
    health: { type: Object, required: true },
    metrics: { type: Array, default: () => [] },
    selected: { type: Boolean, default: false }
  },
  computed: {
    nextText () {
      if (!this.src) return '—'
      if (this.src.state === 'paused') return 'paused'
      return this.src.next_slot ? roWhen(this.src.next_slot) : 'no hour set'
    }
  },
  methods: { roWhen, runLabel, runTone }
}
</script>
